import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Minus,
  ArrowRight,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/quintadena-alternative";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Quintadena / QuoteWerks Alternative for UK Builders",
  "description": "Looking for a Quintadena or QuoteWerks alternative for UK builders? The Proper Tool offers construction-specific AI quoting with a 31-category UK cost library and a 14-day free trial from £29/mo.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Compare", "item": "https://thepropertool.com/comparisons" },
    { "@type": "ListItem", "position": 3, "name": "Quintadena Alternative", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best Quintadena or QuoteWerks alternative for UK builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For UK residential builders who need construction-specific quoting rather than a generic quoting and CRM tool, The Proper Tool is the strongest Quintadena and QuoteWerks alternative. Quintadena is a UK agency reselling QuoteWerks, a US-origin generic quoting platform not designed for construction. The Proper Tool has a 31-category UK construction cost library, AI scope generation from drawings or descriptions, and starts at £29/month with a 14-day free trial.",
      },
    },
    {
      "@type": "Question",
      "name": "What is Quintadena?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Quintadena is a UK agency that resells and implements QuoteWerks, a US-origin quoting and CRM platform. QuoteWerks is a generic quoting tool used across multiple industries, not a construction-specific product. Quintadena acts as the UK implementation partner for businesses that need a local support contact for QuoteWerks.",
      },
    },
    {
      "@type": "Question",
      "name": "Is QuoteWerks suitable for UK residential construction?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "QuoteWerks is a generic quoting and CRM platform used across multiple industries including IT, manufacturing, and professional services. It is not built for construction and has no UK residential construction cost library, no AI scope generation from drawings, and no understanding of construction scope-of-works structure. For UK residential builders, it is a poor fit.",
      },
    },
    {
      "@type": "Question",
      "name": "Does Quintadena or QuoteWerks have a self-serve trial?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "QuoteWerks has a trial version, but setup requires installation and configuration through the Quintadena agency relationship rather than a self-serve cloud signup. The Proper Tool offers a 14-day free trial with no credit card required, accessible immediately from the browser.",
      },
    },
    {
      "@type": "Question",
      "name": "Can QuoteWerks read construction drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. QuoteWerks is a generic quoting platform and does not read architectural drawings or structural plans. The Proper Tool reads uploaded PDF drawings using AI, extracting beam sizes, floor areas, and dimensions to generate a more accurate and complete construction quote.",
      },
    },
  ],
};

type TriState = "yes" | "partial" | "no";

interface ComparisonRow {
  feature: string;
  quintadena: string;
  quintadenaState: TriState;
  tpt: string;
  tptState: TriState;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: "Built for construction quoting", quintadena: "No: generic quoting/CRM", quintadenaState: "no", tpt: "Yes: UK residential construction", tptState: "yes" },
  { feature: "UK construction cost library", quintadena: "No", quintadenaState: "no", tpt: "Yes: 31 UK trade categories", tptState: "yes" },
  { feature: "AI scope generation from description", quintadena: "No: manual entry only", quintadenaState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Reads structural drawings and PDFs", quintadena: "No", quintadenaState: "no", tpt: "Yes: AI interpretation", tptState: "yes" },
  { feature: "Scope completeness validation", quintadena: "No", quintadenaState: "no", tpt: "Yes: built-in expert review", tptState: "yes" },
  { feature: "Labour / materials / plant split", quintadena: "No", quintadenaState: "no", tpt: "Yes: per line item", tptState: "yes" },
  { feature: "Self-serve cloud trial", quintadena: "No: agency-mediated", quintadenaState: "no", tpt: "Yes: 14-day free trial", tptState: "yes" },
  { feature: "Transparent published pricing", quintadena: "Limited: agency-quoted", quintadenaState: "partial", tpt: "Yes: from £29/month", tptState: "yes" },
  { feature: "Direct customer support", quintadena: "Via reseller (Quintadena)", quintadenaState: "partial", tpt: "Yes: direct", tptState: "yes" },
  { feature: "US-origin product", quintadena: "Yes: QuoteWerks is US-based", quintadenaState: "no", tpt: "No: UK-built", tptState: "yes" },
  { feature: "Time to first quote", quintadena: "Setup and agency engagement needed", quintadenaState: "no", tpt: "Under 10 minutes", tptState: "yes" },
  { feature: "Credit card required to trial", quintadena: "Agency engagement required", quintadenaState: "no", tpt: "No", tptState: "yes" },
];

function Cell({ value, state }: { value: string; state: TriState }) {
  if (state === "yes") return (
    <span className="flex items-start gap-1.5 text-green-600 dark:text-green-400 font-medium">
      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  if (state === "no") return (
    <span className="flex items-start gap-1.5 text-red-500 dark:text-red-400">
      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  return (
    <span className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
}

const WHY_LOOK = [
  {
    title: "QuoteWerks is a generic quoting platform, not a construction tool",
    desc: "QuoteWerks, the product Quintadena resells, is a multi-industry quoting and CRM platform used across IT, manufacturing, and professional services. It has no UK residential construction cost library, no understanding of construction scopes of works, and no ability to quote from drawings or project descriptions.",
  },
  {
    title: "US-origin product with no UK construction pricing",
    desc: "QuoteWerks was built in the US for generic quoting across industries. UK residential construction rates, trade categories, and scope conventions are not built in. Builders would need to configure their own pricing from scratch.",
  },
  {
    title: "Agency-resold with no self-serve access",
    desc: "Quintadena acts as a UK reseller and implementation partner for QuoteWerks. There is no self-serve cloud trial or direct signup. Getting started requires engaging with the agency, which adds friction and cost compared to a purpose-built SaaS quoting tool.",
  },
  {
    title: "Thin construction-specific product features",
    desc: "QuoteWerks is designed for generic quoting across industries. Construction-specific features like drawing analysis, scope validation, UK trade category pricing, and labour/materials/plant breakdown are not part of the product. These require either manual configuration or third-party add-ons.",
  },
];

const STAY_ON_QUINTADENA = [
  "You are already on QuoteWerks for your business and need a UK implementation partner to support your existing setup.",
  "Your quoting needs are genuinely cross-industry rather than construction-specific and QuoteWerks fits your workflow.",
  "You have already invested significant time configuring QuoteWerks for construction through Quintadena and have working templates.",
];

const SWITCH_REASONS = [
  "You need a tool built specifically for UK residential construction, not a generic cross-industry quoting platform.",
  "You want UK construction pricing built in, not a blank slate you have to configure from scratch.",
  "You want to generate construction scopes from drawings or descriptions without manual line-by-line entry.",
  "You want direct self-serve cloud access with a 14-day free trial and no agency engagement required.",
  "You want direct support from the product team rather than a reseller.",
  "You want transparent pricing without going through an agency quotation process.",
];

const FAQS = [
  {
    q: "What is the best Quintadena or QuoteWerks alternative for UK builders?",
    a: "For UK residential builders who need construction-specific quoting, The Proper Tool is the strongest Quintadena and QuoteWerks alternative. Quintadena is a UK agency reselling QuoteWerks, a US-origin generic quoting platform not designed for construction. The Proper Tool has a 31-category UK construction cost library, AI scope generation, and starts at £29/month with a 14-day free trial.",
  },
  {
    q: "What is Quintadena?",
    a: "Quintadena is a UK agency that resells and implements QuoteWerks, a US-origin quoting and CRM platform. QuoteWerks is a generic quoting tool used across multiple industries, not a construction-specific product.",
  },
  {
    q: "Is QuoteWerks suitable for UK residential construction?",
    a: "QuoteWerks is a generic quoting and CRM platform used across multiple industries. It is not built for construction and has no UK residential construction cost library, no AI scope generation from drawings, and no understanding of construction scope-of-works structure.",
  },
  {
    q: "Does Quintadena or QuoteWerks have a self-serve trial?",
    a: "Getting started with QuoteWerks via Quintadena requires agency engagement rather than a self-serve cloud signup. The Proper Tool offers a 14-day free trial with no credit card required, accessible immediately from the browser.",
  },
  {
    q: "Can QuoteWerks read construction drawings?",
    a: "No. QuoteWerks is a generic quoting platform and does not read architectural drawings or structural plans. The Proper Tool reads uploaded PDF drawings using AI, extracting beam sizes, floor areas, and dimensions to generate a more complete construction quote.",
  },
];

export default function QuintadenaAlternativePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Quintadena / QuoteWerks Alternative for UK Builders: Construction-Specific Quoting | The Proper Tool"
        description="Looking for a Quintadena or QuoteWerks alternative for UK residential builders? The Proper Tool offers construction-specific AI quoting with a 31-category UK cost library and a 14-day free trial from £29/mo."
        canonical="/quintadena-alternative"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Quintadena Alternative</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Comparison</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Quintadena / QuoteWerks Alternative for UK Builders
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Quintadena is a UK agency reselling QuoteWerks, a US-origin generic quoting platform. If you are a UK residential builder who needs construction-specific quoting with UK pricing built in, here is how The Proper Tool compares.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no credit card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#comparison-table">See the comparison</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · From £29/mo · No credit card required</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why UK builders look for a Quintadena / QuoteWerks alternative</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              QuoteWerks via Quintadena is a generic quoting platform. For UK residential builders, the limitations are fundamental:
            </p>
            <div className="space-y-4">
              {WHY_LOOK.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="comparison-table">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Proper Tool vs Quintadena / QuoteWerks: feature comparison</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A factual side-by-side comparison for UK residential builders.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">Quintadena / QuoteWerks</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.feature}</td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.quintadena} state={row.quintadenaState} /></td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.tpt} state={row.tptState} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
              Competitor pricing and feature information is based on publicly available data at time of writing. Prices and features may have changed. Verify directly with Quintadena before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pricing comparison</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Quintadena's pricing for QuoteWerks is agency-quoted and not published on their website. Getting started requires engaging with the agency. The Proper Tool starts at £29 per month with transparent, published pricing and a 14-day free trial with no credit card required.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-border p-6 bg-muted/20">
                <h3 className="font-bold text-lg mb-3">Quintadena / QuoteWerks</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Pricing quoted via agency, not published</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />No self-serve trial: requires agency engagement</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Generic quoting platform, not construction-specific</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />US-origin product with no UK construction pricing</li>
                </ul>
              </div>
              <div className="rounded-xl border border-primary bg-primary/5 p-6">
                <h3 className="font-bold text-lg mb-3 text-primary">The Proper Tool</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />From £29/month, published and transparent</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />14-day free trial, no credit card required</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />UK-built for residential construction from day one</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />Direct support, no reseller layer</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Who should stay on Quintadena / QuoteWerks</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              QuoteWerks via Quintadena serves a specific use case. If the following describes your situation, it may still be appropriate:
            </p>
            <div className="space-y-3">
              {STAY_ON_QUINTADENA.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Reasons UK builders choose The Proper Tool over Quintadena / QuoteWerks</h2>
            <p className="text-lg text-muted-foreground mb-8">For residential construction, a purpose-built tool beats a generic quoting platform every time.</p>
            <div className="space-y-4">
              {SWITCH_REASONS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20 bg-primary/5 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try The Proper Tool free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              UK-built construction quoting software. Generate a professional, fully itemised construction quote from drawings or a description in under 10 minutes.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free, no credit card needed
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · Cancel any time</p>
          </div>
        </section>

        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Disclaimer:</strong> Competitor pricing and feature information on this page is based on publicly available data and is correct to the best of our knowledge at the time of publication. Prices and features may have changed. We recommend verifying current details directly with Quintadena before making a purchasing decision.
            </p>
          </div>
        </section>

        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Also comparing</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/best/quoting-software-for-uk-builders", label: "Full comparison: all quoting tools for UK builders" },
                { href: "/tradify-alternative", label: "Tradify Alternative" },
                { href: "/fergus-alternative", label: "Fergus Alternative" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="rounded-lg border border-border p-4 text-sm font-medium hover:border-primary hover:text-primary transition-colors"
                >
                  {link.label} →
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
